import * as React from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { MobileStickyActionBar } from "@/components/ui/mobile-sticky-action-bar"
import type { PageProps } from "@/types/inertia"

type Props = {
  timezones: string[]
}

export default function AdminUserNew({ timezones }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm({
    name: "",
    email: "",
    password: "",
    timezone: "",
    admin: false,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post("/admin/users", {
      preserveScroll: true,
    })
  }

  const alert = props.flash?.alert

  return (
    <>
      <Head title="New User">
        <meta name="description" content="Create a new user account." />
        <meta property="og:title" content="New User" />
        <meta property="og:description" content="Create a new user account." />
      </Head>
      <AppShell>
        <PageHeader
          title="New User"
          description="Create a new user account in this system."
          backHref="/admin/users"
          backLabel="Back to users"
        />

        {alert && <p className="mt-4 text-sm text-danger-display">{alert}</p>}

        <section className="mt-6 max-w-md">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name">Full Name</label>
              <Input
                id="name"
                type="text"
                autoComplete="name"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <p className="text-xs text-danger-display">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="email">Email Address</label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={form.data.email}
                onChange={(e) => form.setData("email", e.target.value)}
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-xs text-danger-display">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="password">Password</label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                value={form.data.password}
                onChange={(e) => form.setData("password", e.target.value)}
                aria-invalid={!!errors.password}
              />
              {errors.password && (
                <p className="text-xs text-danger-display">{errors.password}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="timezone">Timezone</label>
              <Select
                id="timezone"
                value={form.data.timezone}
                onChange={(e) => form.setData("timezone", e.target.value)}
                aria-invalid={!!errors.timezone}
              >
                <option value="">Select a timezone...</option>
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </Select>
              {errors.timezone && (
                <p className="text-xs text-danger-display">{errors.timezone}</p>
              )}
            </div>

            <div className="pt-2">
              <label className="flex items-center gap-3 font-normal text-ink-body">
                <Checkbox
                  id="admin"
                  checked={form.data.admin}
                  onChange={(e) => form.setData("admin", e.target.checked)}
                />
                <span>Administrator</span>
              </label>
              {errors.admin && (
                <p className="mt-1 text-xs text-danger-display">{errors.admin}</p>
              )}
            </div>

            <MobileStickyActionBar>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? "Creating..." : "Create user"}
              </Button>
            </MobileStickyActionBar>
          </form>
        </section>
      </AppShell>
    </>
  )
}
